// Your own VRM body in VR (VRAvatarBody's VRM branch): idles with the shared
// library clip, hair/clothes swing on spring bones, and the head is hidden with
// three-vrm's first-person meshes so it never blocks your view.
import React, { useEffect, useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { useGLTF } from '@react-three/drei';
import * as THREE from 'three';
import { useVrmModel } from '../../avatar/vrm/useAvatarModel';
import { VrmFace, VrmFirstPersonView } from '../../avatar/vrm/vrmRuntime';
import { VRM_BASE_CLIPS } from '../../avatar/vrm/vrmFormat';
import { LIBRARY_SOURCE_MODEL } from '../../config/emotes';
import { captureRest, fitLibraryClip, isTripoLibraryClip } from '../../avatar/libraryRetarget';

interface Props {
  url: string;
  idleUrl?: string | null;
  bodyRef: React.RefObject<THREE.Group | null>;
}

const VrmVRBody: React.FC<Props> = ({ url, idleUrl, bodyRef }) => {
  const inst = useVrmModel(url);
  const { vrm, scene } = inst;
  const clipUrl = idleUrl && isTripoLibraryClip(idleUrl) ? idleUrl : VRM_BASE_CLIPS.idle;
  const idleGltf = useGLTF(clipUrl);
  const librarySkeleton = useGLTF(LIBRARY_SOURCE_MODEL);

  // Rest pose before the mixer ever touches the scene.
  const rest = useMemo(() => captureRest(scene), [scene]);
  const mixer = useMemo(() => new THREE.AnimationMixer(scene), [scene]);
  useEffect(() => {
    const src = idleGltf.animations[0];
    if (!src) return;
    const clip = fitLibraryClip(src, librarySkeleton.scene, rest);
    clip.name = 'idle';
    const action = mixer.clipAction(clip);
    action.timeScale = 0.65;
    action.reset().play();
    return () => { action.stop(); mixer.uncacheClip(clip); };
  }, [idleGltf, librarySkeleton, rest, mixer]);

  const face = useMemo(() => new VrmFace(vrm, `vr|${url}`), [vrm, url]);
  const fp = useMemo(() => new VrmFirstPersonView(vrm), [vrm]);
  const headShown = useRef(false);
  useEffect(() => {
    scene.position.set(0, 0, 0);
    scene.scale.setScalar(1);
    fp.setFirstPerson(true);
    // The selfie camera "un-hides the head" via __vrHeadBone.scale.set(1|0)
    // (see VRViewer captureFrom); give it the same switch for a VRM.
    const shim = { scale: { set: (x: number) => { headShown.current = x > 0; fp.setFirstPerson(x === 0); } } };
    (window as any).__vrHeadBone = shim;
    return () => {
      if ((window as any).__vrHeadBone === shim) (window as any).__vrHeadBone = null;
      mixer.stopAllAction();
    };
  }, [scene, fp, mixer]);

  useFrame((_, delta) => {
    mixer.update(Math.min(delta, 0.1));
    // Mouth is hidden in first person; springs, blinks (for selfies) still run.
    face.update(delta, 0, null);
  });

  return (
    <group ref={bodyRef} name="vr-avatar-body">
      <primitive object={scene} />
    </group>
  );
};

export default VrmVRBody;
